'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useRouter } from '@/shared/navigation'
import { dispatchDeliveryToDelman } from '@/features/kitchen/api/dispatchDelivery'
import {
  generateDispatchDeliveries,
  type GenerateDeliveriesResponse,
} from '@/features/kitchen/api/generateDispatchDeliveries'
import {
  fetchKitchenDelmans,
  fetchKitchenOutlets,
  fetchKitchenStockLots,
  fetchOutletPlan,
  type ApiOutlet,
  type ApiOutletPlanLine,
} from '@/features/kitchen/api/getDispatchOptions'
import { fetchTodayDeliveries } from '@/features/kitchen/api/getTodayDispatch'
import { downloadTodayDispatchPdf } from '@/features/kitchen/api/downloadTodayDispatchPdf'
import { fetchTodayProductionPlan } from '@/features/kitchen/api/getTodayProduction'
import { saveDispatchDelivery } from '@/features/kitchen/api/saveDispatchDelivery'
import { shouldUseKitchenApi } from '@/features/kitchen/api/kitchenApiMode'
import { mapDeliveriesFromApi, type ApiShipment } from '@/features/kitchen/api/mapDispatchFromApi'
import { loadDispatchShipments, saveDispatchShipments } from '@/features/kitchen/storage/dispatchStorage'
import {
  formatTripDateLabel,
  kitchenTodayISO,
  kitchenTomorrowISO,
} from '@/features/kitchen/lib/kitchenDates'
import {
  findDadakanConflicts,
  isTripDateToday,
  type DadakanConflict,
} from '@/features/kitchen/lib/dadakanWarning'
import { CollapsibleCategoryBlock } from '@/features/kitchen/CollapsibleCategoryBlock'
import {
  buildPlanCatalogFromTodayPlan,
  groupProductLinesByCategory,
  type PlanCatalogItem,
} from '@/features/kitchen/productionPlanLayout'
import { ActionSummaryCard } from '@/shared/components/ActionSummaryCard'
import { EmptyState } from '@/shared/components/EmptyState'
import { FlowStepper } from '@/shared/components/FlowStepper'
import { SkeletonList } from '@/shared/components/Skeleton'
import { QtyStepper } from '@/shared/components/QtyStepper'
import { StickyActionFooter } from '@/shared/components/StickyActionFooter'
import { PinKeypadModal } from '@/shared/components/PinKeypadModal'
import { Toast } from '@/shared/components/Toast'
import { getKitchenDevice } from '@/shared/auth/deviceStorage'
import { kitchenTerms } from '@/features/kitchen/kitchenTerms'
import { consumeLastSyncQueued } from '@/shared/sync/kitchenSync'
import { GenerateResultSheet } from '@/features/kitchen/GenerateResultSheet'

type ShipmentStatus = 'draft' | 'prepared' | 'in_transit' | 'received'

type ShipmentLine = {
  id: string
  productName: string
  productionDate: string
  qty: number
}

type Shipment = {
  id: string
  deliveryId?: string
  storeName: string
  delmanName: string
  tripDate?: string
  status: ShipmentStatus
  lines: ShipmentLine[]
  updatedAt: string
}

type StockItem = {
  productName: string
  productionDate: string
  stockAvailable: number
}

type View = 'list' | 'edit'
type EditorStep = 'edit' | 'review'
type ListFilter = 'pending' | 'in_transit' | 'received'

const DEFAULT_OUTLETS = ['Toko Menteng', 'Toko Kemang', 'Toko Cipete', 'Toko Senayan', 'Toko BSD'] as const

const MOCK_OUTLET_PLANS: Record<string, ApiOutletPlanLine[]> = {
  'Toko Menteng': [
    { productName: 'Roti coklat', planQty: 40 },
    { productName: 'Donat meses', planQty: 35 },
  ],
  'Toko Kemang': [
    { productName: 'Roti coklat', planQty: 30 },
    { productName: 'Roti keju', planQty: 25 },
  ],
  'Toko Cipete': [
    { productName: 'Donat meses', planQty: 28 },
    { productName: 'Roti keju', planQty: 20 },
  ],
  'Toko Senayan': [{ productName: 'Roti coklat', planQty: 50 }],
  'Toko BSD': [
    { productName: 'Roti coklat', planQty: 35 },
    { productName: 'Donat meses', planQty: 30 },
    { productName: 'Roti keju', planQty: 22 },
  ],
}

function sumPlanLines(lines: ApiOutletPlanLine[]): number {
  return lines.reduce((sum, line) => sum + line.planQty, 0)
}

function outletHasHoPlan(outlet: string, plans: Record<string, ApiOutletPlanLine[]>): boolean {
  return (plans[outlet]?.length ?? 0) > 0
}

const DEFAULT_DELMANS = ['Budi Santoso', 'Andi Wijaya', 'Candra Pratama'] as const

const MOCK_DISPATCH_PLAN_CATALOG: PlanCatalogItem[] = [
  { sku: 'Roti coklat', plan: 0, categoryName: 'Roti Manis', categorySortOrder: 1 },
  { sku: 'Roti keju', plan: 0, categoryName: 'Roti Manis', categorySortOrder: 1 },
  { sku: 'Donat meses', plan: 0, categoryName: 'Roti Manis', categorySortOrder: 1 },
  { sku: 'Donat gula', plan: 0, categoryName: 'Roti Manis', categorySortOrder: 1 },
  { sku: 'Croissant', plan: 0, categoryName: 'Pastry', categorySortOrder: 2 },
]

const DISPATCH_FLOW_STEPS = kitchenTerms.dispatch.flowSteps.map((label) => ({ label }))

function dispatchFlowActiveStep(
  status: ShipmentStatus,
  editorStep: EditorStep,
): number {
  if (status === 'received') return DISPATCH_FLOW_STEPS.length + 1
  if (status === 'in_transit') return 3
  if (editorStep === 'review') return 2
  return 1
}

const BASE_STOCK: StockItem[] = [
  { productName: 'Roti coklat', productionDate: '23/06/26', stockAvailable: 120 },
  { productName: 'Roti keju', productionDate: '23/06/26', stockAvailable: 80 },
  { productName: 'Donat gula', productionDate: '23/06/26', stockAvailable: 45 },
  { productName: 'Croissant', productionDate: '23/06/26', stockAvailable: 24 },
  { productName: 'Roti coklat', productionDate: '22/06/26', stockAvailable: 30 },
]

function stockKey(productName: string, productionDate: string): string {
  return `${productName}|${productionDate}`
}

function shipmentStatusLabel(status: ShipmentStatus): string {
  const t = kitchenTerms.dispatch.status
  if (status === 'draft') return t.draft
  if (status === 'prepared') return t.prepared
  if (status === 'in_transit') return t.inTransit
  return t.received
}

function shipmentStatusBadge(status: ShipmentStatus): string {
  if (status === 'draft') return 'badge-draft'
  if (status === 'prepared') return 'badge-warning'
  if (status === 'in_transit') return 'badge-warning'
  return 'badge-success'
}

function shipmentStatusShortLabel(status: ShipmentStatus): string {
  const t = kitchenTerms.dispatch.status
  if (status === 'draft') return t.draftShort
  if (status === 'prepared') return t.preparedShort
  if (status === 'in_transit') return t.inTransitShort
  return t.receivedShort
}

function sortShipmentsForDisplay(shipments: Shipment[]): Shipment[] {
  const rank: Record<ShipmentStatus, number> = {
    prepared: 0,
    draft: 1,
    in_transit: 2,
    received: 3,
  }
  return [...shipments].sort((a, b) => {
    if (rank[a.status] !== rank[b.status]) return rank[a.status] - rank[b.status]
    return a.storeName.localeCompare(b.storeName, 'id')
  })
}

function groupShipmentsByDelman(shipments: Shipment[]): { delmanName: string; shipments: Shipment[] }[] {
  const order: string[] = []
  const map = new Map<string, Shipment[]>()

  for (const shipment of shipments) {
    if (!map.has(shipment.delmanName)) {
      map.set(shipment.delmanName, [])
      order.push(shipment.delmanName)
    }
    map.get(shipment.delmanName)!.push(shipment)
  }

  return order.map((delmanName) => ({
    delmanName,
    shipments: sortShipmentsForDisplay(map.get(delmanName)!),
  }))
}

function formatHandoverTime(iso?: string): string {
  if (!iso) return ''
  const date = new Date(iso)
  const day = new Intl.DateTimeFormat('id-ID', { day: '2-digit', month: '2-digit' }).format(date)
  const time = new Intl.DateTimeFormat('id-ID', { hour: '2-digit', minute: '2-digit' }).format(date)
  return `Diserahkan ${day} ${time}`
}

function formatSavedAt(): string {
  const now = new Date()
  const day = new Intl.DateTimeFormat('id-ID', { day: '2-digit', month: '2-digit' }).format(now)
  const time = new Intl.DateTimeFormat('id-ID', { hour: '2-digit', minute: '2-digit' }).format(now)
  return `Disimpan ${day} ${time}`
}

function getFilledLines(lines: ShipmentLine[]): ShipmentLine[] {
  return lines.filter((line) => line.qty > 0)
}

function productionDateSortKey(productionDate: string): string {
  const [dd, mm, yy] = productionDate.split('/')
  if (!dd || !mm || !yy) return productionDate
  return `20${yy.padStart(2, '0')}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`
}

function totalAvailableForProduct(
  productName: string,
  stockItems: StockItem[],
  remainingStock: Map<string, number>,
): number {
  return stockItems
    .filter((item) => item.productName === productName)
    .reduce(
      (sum, item) =>
        sum + Math.max(0, remainingStock.get(stockKey(item.productName, item.productionDate)) ?? 0),
      0,
    )
}

function totalKitchenStockAvailable(
  stockItems: StockItem[],
  remainingStock: Map<string, number>,
): number {
  return stockItems.reduce(
    (sum, item) =>
      sum + Math.max(0, remainingStock.get(stockKey(item.productName, item.productionDate)) ?? 0),
    0,
  )
}

function buildShipmentLinesFromOutletPlan(
  planLines: ApiOutletPlanLine[],
  stockItems: StockItem[],
  remainingStock: Map<string, number>,
  storeName: string,
  shipments: Shipment[],
  excludeShipmentId?: string | null,
  tripDate?: string | null,
): ShipmentLine[] {
  const seen = new Set<string>()
  const lines: ShipmentLine[] = []
  const baseId = Date.now()
  let lineIndex = 0

  for (const plan of planLines) {
    if (seen.has(plan.productName)) continue
    seen.add(plan.productName)

    const planRemaining = Math.max(
      0,
      plan.planQty -
        qtyUsedForOutletProduct(shipments, storeName, plan.productName, {
          excludeShipmentId,
          tripDate,
        }),
    )
    if (planRemaining <= 0) continue

    const stockCap = totalAvailableForProduct(plan.productName, stockItems, remainingStock)
    const qty = Math.min(planRemaining, stockCap)
    if (qty <= 0) continue

    const oldestLot = stockItems
      .filter((item) => item.productName === plan.productName)
      .filter(
        (item) =>
          (remainingStock.get(stockKey(item.productName, item.productionDate)) ?? 0) > 0,
      )
      .sort((a, b) =>
        productionDateSortKey(a.productionDate).localeCompare(
          productionDateSortKey(b.productionDate),
        ),
      )[0]

    lines.push({
      id: `line-${baseId}-${lineIndex}`,
      productName: plan.productName,
      productionDate: oldestLot?.productionDate ?? '-',
      qty,
    })
    lineIndex += 1
  }

  return lines
}

function buildShipmentLinesFromStock(
  stockItems: StockItem[],
  remainingStock: Map<string, number>,
): ShipmentLine[] {
  const baseId = Date.now()

  return stockItems
    .filter((item) => (remainingStock.get(stockKey(item.productName, item.productionDate)) ?? 0) > 0)
    .map((item, index) => ({
      id: `line-${baseId}-${index}`,
      productName: item.productName,
      productionDate: item.productionDate,
      qty: 0,
    }))
}

function buildInitialShipmentLines(
  planLines: ApiOutletPlanLine[],
  stockItems: StockItem[],
  shipments: Shipment[],
  storeName: string,
  excludeShipmentId?: string | null,
  useLedgerStock = false,
  tripDate?: string | null,
): ShipmentLine[] {
  const remaining = computeRemainingStock(
    shipments,
    excludeShipmentId,
    stockItems,
    useLedgerStock,
  )
  if (planLines.length > 0) {
    return buildShipmentLinesFromOutletPlan(
      planLines,
      stockItems,
      remaining,
      storeName,
      shipments,
      excludeShipmentId,
      tripDate,
    )
  }
  return buildShipmentLinesFromStock(stockItems, remaining)
}

function isEditableStatus(status: ShipmentStatus): boolean {
  return status === 'draft' || status === 'prepared'
}

function isActiveAtKitchen(status: ShipmentStatus): boolean {
  return status === 'draft' || status === 'prepared'
}

function getStockItemForProduct(
  productName: string,
  productionDate: string | undefined,
  baseStock: StockItem[],
): StockItem | undefined {
  if (productionDate) {
    const exact = baseStock.find(
      (item) => item.productName === productName && item.productionDate === productionDate,
    )
    if (exact) return exact
  }
  return baseStock.find((item) => item.productName === productName)
}

function qtyUsedForOutletProduct(
  shipments: Shipment[],
  storeName: string,
  productName: string,
  options?: {
    excludeShipmentId?: string | null
    excludeLineId?: string
    /** Jika diisi, hanya hitung kiriman dengan trip_date sama (kuota plan per tanggal). */
    tripDate?: string | null
  },
): number {
  const trip = options?.tripDate?.trim()
  return shipments
    .filter((s) => {
      if (s.storeName !== storeName || s.id === options?.excludeShipmentId) return false
      if (trip && (s.tripDate || '').trim() !== trip) return false
      return true
    })
    .flatMap((s) => getFilledLines(s.lines))
    .filter((l) => l.productName === productName && l.id !== options?.excludeLineId)
    .reduce((sum, l) => sum + l.qty, 0)
}

/** Stok tersisa. Mode ledger: stok API sudah neto; kurangi hanya draft/siap serah yang belum keluar ledger. */
function computeRemainingStock(
  shipments: Shipment[],
  excludeShipmentId?: string | null,
  baseStock: StockItem[] = BASE_STOCK,
  subtractOnlyDraftAndPrepared = false,
): Map<string, number> {
  const remaining = new Map(
    baseStock.map((item) => [stockKey(item.productName, item.productionDate), item.stockAvailable]),
  )

  for (const shipment of shipments) {
    if (shipment.id === excludeShipmentId) continue
    if (subtractOnlyDraftAndPrepared && !isActiveAtKitchen(shipment.status)) continue
    for (const line of getFilledLines(shipment.lines)) {
      const stockItem = getStockItemForProduct(line.productName, line.productionDate, baseStock)
      if (!stockItem) continue
      const key = stockKey(stockItem.productName, stockItem.productionDate)
      remaining.set(key, (remaining.get(key) ?? 0) - line.qty)
    }
  }

  return remaining
}

function getLineStockLimit(
  shipments: Shipment[],
  shipmentId: string,
  productName: string,
  productionDate: string,
  baseStock: StockItem[] = BASE_STOCK,
  subtractOnlyDraftAndPrepared = false,
): number {
  const base = getStockItemForProduct(productName, productionDate, baseStock)
  if (!base) return 0

  const remaining = computeRemainingStock(
    shipments,
    shipmentId,
    baseStock,
    subtractOnlyDraftAndPrepared,
  )
  const key = stockKey(base.productName, base.productionDate)
  return Math.max(0, remaining.get(key) ?? base.stockAvailable)
}

export function KitchenDispatchPage() {
  const router = useRouter()
  const useApi = shouldUseKitchenApi()
  const device = getKitchenDevice()
  const [shipments, setShipments] = useState<Shipment[]>(() =>
    useApi ? [] : loadDispatchShipments(),
  )
  const [isLoading, setIsLoading] = useState(useApi)
  const [apiLoadError, setApiLoadError] = useState<string | null>(null)
  const [view, setView] = useState<View>('list')
  const [listFilter, setListFilter] = useState<ListFilter>('pending')
  const [editorStep, setEditorStep] = useState<EditorStep>('edit')
  const [selectedShipmentId, setSelectedShipmentId] = useState<string | null>(null)
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [outletOptions, setOutletOptions] = useState<string[]>(useApi ? [] : [...DEFAULT_OUTLETS])
  const [delmanOptions, setDelmanOptions] = useState<string[]>(useApi ? [] : [...DEFAULT_DELMANS])
  const [stockItems, setStockItems] = useState<StockItem[]>(useApi ? [] : BASE_STOCK)
  const [stockLoaded, setStockLoaded] = useState(!useApi)
  const [outletList, setOutletList] = useState<ApiOutlet[]>([])
  const [outletPlan, setOutletPlan] = useState<Record<string, number> | null>(null)
  const [outletPlansByName, setOutletPlansByName] = useState<Record<string, ApiOutletPlanLine[]>>({})
  const [isLoadingCreateOutletPlans, setIsLoadingCreateOutletPlans] = useState(false)
  const [isStockOpen, setIsStockOpen] = useState(false)
  const [selectedOutlets, setSelectedOutlets] = useState<string[]>([])
  const [createTripDate, setCreateTripDate] = useState<'today' | 'tomorrow'>('today')
  const [tomorrowPlanAvailable, setTomorrowPlanAvailable] = useState(false)
  const [newDelman, setNewDelman] = useState<string>(useApi ? '' : DEFAULT_DELMANS[0])
  const [createError, setCreateError] = useState<string | null>(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [isDispatching, setIsDispatching] = useState(false)
  const [isPinModalOpen, setIsPinModalOpen] = useState(false)
  const [dadakanWarning, setDadakanWarning] = useState<{
    action: 'generate' | 'open-pin'
    conflicts: DadakanConflict[]
  } | null>(null)
  const [isSavingShipment, setIsSavingShipment] = useState(false)
  const [toast, setToast] = useState<string | null>(null)
  const [generateResult, setGenerateResult] = useState<GenerateDeliveriesResponse | null>(null)
  const [generateResultOutletCount, setGenerateResultOutletCount] = useState(0)
  const [generateResultTripLabel, setGenerateResultTripLabel] = useState<string | undefined>()
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false)
  const [planCatalog, setPlanCatalog] = useState<PlanCatalogItem[]>(
    useApi ? [] : MOCK_DISPATCH_PLAN_CATALOG,
  )

  useEffect(() => {
    if (!useApi || !device?.deviceId) return

    let cancelled = false
    fetchTodayProductionPlan(device.deviceId)
      .then((plan) => {
        if (cancelled) return
        setPlanCatalog(buildPlanCatalogFromTodayPlan(plan))
      })
      .catch(() => {
        if (!cancelled) setPlanCatalog([])
      })

    return () => {
      cancelled = true
    }
  }, [useApi, device?.deviceId])

  useEffect(() => {
    if (useApi) return
    saveDispatchShipments(shipments)
  }, [shipments, useApi])

  const reloadDeliveries = useCallback(() => {
    if (!useApi || !device?.deviceId) return Promise.resolve()
    setApiLoadError(null)
    return fetchTodayDeliveries(device.deviceId)
      .then((data) => {
        const mapped: Shipment[] = mapDeliveriesFromApi(data.deliveries).map((s: ApiShipment) => ({
          id: s.id,
          deliveryId: s.deliveryId,
          storeName: s.storeName,
          delmanName: s.delmanName,
          tripDate: s.tripDate,
          status: s.status,
          lines: s.lines,
          updatedAt: s.updatedAt,
        }))
        setShipments(mapped)
      })
      .catch((err) => {
        setApiLoadError(
          err instanceof Error ? err.message : kitchenTerms.dispatch.errors.loadFailed,
        )
      })
  }, [useApi, device?.deviceId])

  useEffect(() => {
    if (!useApi || !device?.deviceId) {
      setIsLoading(false)
      return
    }

    let cancelled = false
    setIsLoading(true)
    reloadDeliveries().finally(() => {
      if (!cancelled) setIsLoading(false)
    })

    return () => {
      cancelled = true
    }
  }, [useApi, device?.deviceId, reloadDeliveries])

  useEffect(() => {
    if (!useApi || !device?.deviceId) return undefined
    const hasInTransit = shipments.some((s) => s.status === 'in_transit')
    if (!hasInTransit) return undefined
    const timer = window.setInterval(() => void reloadDeliveries(), 5_000)
    return () => window.clearInterval(timer)
  }, [useApi, device?.deviceId, shipments, reloadDeliveries])

  useEffect(() => {
    if (!useApi || !device?.deviceId) return

    let cancelled = false
    Promise.all([
      fetchKitchenOutlets(device.deviceId),
      fetchKitchenDelmans(device.deviceId),
    ])
      .then(([outlets, delmans]) => {
        if (cancelled) return
        setOutletList(outlets)
        setOutletOptions(outlets.map((o) => o.name))
        setDelmanOptions(delmans.map((d) => d.name))
      })
      .catch(() => {
        if (cancelled) return
        setOutletList([])
        setOutletOptions([])
        setDelmanOptions([])
      })

    return () => {
      cancelled = true
    }
  }, [useApi, device?.deviceId])

  const reloadStock = useCallback(() => {
    if (!useApi || !device?.deviceId) return
    fetchKitchenStockLots(device.deviceId)
      .then((stock) => {
        setStockItems(
          stock.map((s) => ({
            productName: s.productName,
            productionDate: s.productionDate,
            stockAvailable: s.stockAvailable,
          })),
        )
      })
      .catch(() => setStockItems([]))
      .finally(() => setStockLoaded(true))
  }, [useApi, device?.deviceId])

  useEffect(() => {
    reloadStock()
  }, [reloadStock, shipments])

  useEffect(() => {
    if (isCreateOpen) reloadStock()
  }, [isCreateOpen, reloadStock])

  useEffect(() => {
    const onFocus = () => {
      reloadStock()
      void reloadDeliveries()
    }
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        reloadStock()
        void reloadDeliveries()
      }
    }
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [reloadStock, reloadDeliveries])

  const selectedShipmentTripDate = shipments.find((s) => s.id === selectedShipmentId)?.tripDate

  useEffect(() => {
    if (!useApi || !device?.deviceId || !selectedShipmentId) {
      setOutletPlan(null)
      return
    }
    const shipment = shipments.find((s) => s.id === selectedShipmentId)
    const outlet = outletList.find((o) => o.name === shipment?.storeName)
    if (!shipment || !outlet) {
      setOutletPlan(null)
      return
    }

    const planDate = (selectedShipmentTripDate || kitchenTodayISO()).trim()
    let cancelled = false
    fetchOutletPlan(device.deviceId, outlet.id, planDate)
      .then((lines) => {
        if (cancelled) return
        const map: Record<string, number> = {}
        for (const line of lines) map[line.productName] = line.planQty
        setOutletPlan(map)
      })
      .catch(() => {
        if (!cancelled) setOutletPlan(null)
      })

    return () => {
      cancelled = true
    }
  }, [
    useApi,
    device?.deviceId,
    selectedShipmentId,
    selectedShipmentTripDate,
    shipments,
    outletList,
  ])

  useEffect(() => {
    if (!selectedShipmentId) return
    const shipment = shipments.find((s) => s.id === selectedShipmentId)
    if (!shipment) return
    if (shipment.lines.length > 0) return
    if (!isEditableStatus(shipment.status)) return
    // Mode API: qty awal hanya dari Generate distribusi, bukan isi penuh per produk.
    if (useApi) return

    const planLines: ApiOutletPlanLine[] = useApi
      ? outletPlan
        ? Object.entries(outletPlan).map(([productName, planQty]) => ({ productName, planQty }))
        : []
      : MOCK_OUTLET_PLANS[shipment.storeName] ?? []

    if (useApi && outletPlan === null) return

    const lines = buildInitialShipmentLines(
      planLines,
      stockItems,
      shipments,
      shipment.storeName,
      selectedShipmentId,
      useApi,
      shipment.tripDate,
    )
    if (lines.length === 0) return

    setShipments((prev) =>
      prev.map((s) => (s.id === selectedShipmentId ? { ...s, lines } : s)),
    )
  }, [selectedShipmentId, outletPlan, useApi, stockItems, shipments])

  useEffect(() => {
    if (!isCreateOpen) {
      setTomorrowPlanAvailable(false)
      return
    }

    if (!useApi || !device?.deviceId) {
      setTomorrowPlanAvailable(true)
      return
    }

    let cancelled = false
    fetchTodayProductionPlan(device.deviceId, kitchenTomorrowISO())
      .then((plan) => {
        if (cancelled) return
        setTomorrowPlanAvailable((plan.plans?.length ?? 0) > 0)
      })
      .catch(() => {
        if (!cancelled) setTomorrowPlanAvailable(false)
      })

    return () => {
      cancelled = true
    }
  }, [isCreateOpen, useApi, device?.deviceId])

  useEffect(() => {
    if (!tomorrowPlanAvailable && createTripDate === 'tomorrow') {
      setCreateTripDate('today')
    }
  }, [tomorrowPlanAvailable, createTripDate])

  useEffect(() => {
    if (!isCreateOpen) return

    if (!useApi) {
      setOutletPlansByName(MOCK_OUTLET_PLANS)
      return
    }

    if (!device?.deviceId || outletList.length === 0) {
      setOutletPlansByName({})
      return
    }

    const planDate =
      createTripDate === 'tomorrow' ? kitchenTomorrowISO() : kitchenTodayISO()
    let cancelled = false
    setIsLoadingCreateOutletPlans(true)
    Promise.all(
      outletList.map(async (outlet) => {
        const lines = await fetchOutletPlan(device.deviceId, outlet.id, planDate)
        return [outlet.name, lines] as const
      }),
    )
      .then((entries) => {
        if (cancelled) return
        setOutletPlansByName(Object.fromEntries(entries))
      })
      .catch(() => {
        if (!cancelled) setOutletPlansByName({})
      })
      .finally(() => {
        if (!cancelled) setIsLoadingCreateOutletPlans(false)
      })

    return () => {
      cancelled = true
    }
  }, [isCreateOpen, useApi, device?.deviceId, outletList, createTripDate])

  useEffect(() => {
    if (!isCreateOpen || isLoadingCreateOutletPlans) return
    setSelectedOutlets((prev) => prev.filter((name) => outletHasHoPlan(name, outletPlansByName)))
  }, [isCreateOpen, isLoadingCreateOutletPlans, outletPlansByName])

  const selectableOutlets = useMemo(
    () => outletOptions.filter((outlet) => outletHasHoPlan(outlet, outletPlansByName)),
    [outletOptions, outletPlansByName],
  )

  const selectedCreatePlanTotal = useMemo(() => {
    return selectedOutlets.reduce((sum, outlet) => sum + sumPlanLines(outletPlansByName[outlet] ?? []), 0)
  }, [selectedOutlets, outletPlansByName])

  const createModalHoPlanTotal = useMemo(() => {
    return selectableOutlets.reduce(
      (sum, outlet) => sum + sumPlanLines(outletPlansByName[outlet] ?? []),
      0,
    )
  }, [selectableOutlets, outletPlansByName])

  const hoPlanProductCount = useMemo(
    () => planCatalog.filter((item) => item.plan > 0).length,
    [planCatalog],
  )

  const hoPlanTotalPcs = useMemo(
    () => planCatalog.reduce((sum, item) => sum + Math.max(0, item.plan), 0),
    [planCatalog],
  )

  const kitchenName = device?.kitchenName ?? 'Dapur'

  const today = useMemo(
    () =>
      new Intl.DateTimeFormat('id-ID', {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      }).format(new Date()),
    [],
  )

  const dismissToast = useCallback(() => setToast(null), [])
  const showToast = useCallback((message: string) => setToast(message), [])

  async function handleDownloadDispatchPdf() {
    if (!useApi) {
      showToast(kitchenTerms.dispatch.toast.pdfNeedsApi)
      return
    }
    setIsDownloadingPdf(true)
    try {
      await downloadTodayDispatchPdf()
      showToast(kitchenTerms.dispatch.toast.pdfSuccess)
    } catch (err) {
      showToast(err instanceof Error ? err.message : kitchenTerms.dispatch.toast.pdfFailed)
    } finally {
      setIsDownloadingPdf(false)
    }
  }

  const selectedShipment = shipments.find((s) => s.id === selectedShipmentId) ?? null

  const pendingShipments = useMemo(
    () => shipments.filter((s) => isActiveAtKitchen(s.status)),
    [shipments],
  )
  const inTransitShipments = useMemo(
    () => shipments.filter((s) => s.status === 'in_transit'),
    [shipments],
  )
  const receivedShipments = useMemo(
    () => shipments.filter((s) => s.status === 'received'),
    [shipments],
  )

  const visibleShipments = useMemo(() => {
    if (listFilter === 'pending') return sortShipmentsForDisplay(pendingShipments)
    if (listFilter === 'in_transit') return sortShipmentsForDisplay(inTransitShipments)
    return sortShipmentsForDisplay(receivedShipments)
  }, [listFilter, pendingShipments, inTransitShipments, receivedShipments])

  const preparedPending = useMemo(
    () => pendingShipments.filter((s) => s.status === 'prepared'),
    [pendingShipments],
  )
  const draftPending = useMemo(
    () => pendingShipments.filter((s) => s.status === 'draft'),
    [pendingShipments],
  )

  const remainingStock = useMemo(
    () => computeRemainingStock(shipments, selectedShipmentId, stockItems, useApi),
    [shipments, selectedShipmentId, stockItems, useApi],
  )

  const totalKitchenStock = useMemo(
    () => totalKitchenStockAvailable(stockItems, remainingStock),
    [stockItems, remainingStock],
  )

  const hasNoKitchenStock = useApi && stockLoaded && totalKitchenStock <= 0

  const createModalPlanPcs = selectedCreatePlanTotal > 0 ? selectedCreatePlanTotal : createModalHoPlanTotal
  const createModalPlanProducts =
    selectedOutlets.length > 0
      ? new Set(
          selectedOutlets.flatMap((outlet) =>
            (outletPlansByName[outlet] ?? []).map((line) => line.productName),
          ),
        ).size
      : hoPlanProductCount

  const visibleStockChips = useMemo(() => {
    return stockItems.map((item) => {
      const key = stockKey(item.productName, item.productionDate)
      const available = remainingStock.get(key) ?? 0
      return { ...item, key, available }
    }).filter((item) => item.available > 0)
  }, [remainingStock, stockItems])

  /** Sisa rencana untuk produk ini ke outlet terpilih (cap distribusi). null = tidak ada cap rencana. */
  function planRemainingForProduct(productName: string, excludeLineId?: string): number | null {
    if (!outletPlan || !selectedShipment) return null
    const planQty = outletPlan[productName] ?? 0
    const used = qtyUsedForOutletProduct(shipments, selectedShipment.storeName, productName, {
      excludeLineId,
      tripDate: selectedShipment.tripDate,
    })
    return Math.max(0, planQty - used)
  }

  /** Batas kirim per produk: rencana HO jika stok cukup, else min(rencana, stok total). */
  function productDispatchCap(productName: string, excludeLineId?: string): number | null {
    if (!outletPlan || !selectedShipment) return null
    const planQty = outletPlan[productName] ?? 0
    if (planQty <= 0) return 0
    const totalStock = totalAvailableForProduct(productName, stockItems, remainingStock)
    const used = qtyUsedForOutletProduct(shipments, selectedShipment.storeName, productName, {
      excludeLineId,
      tripDate: selectedShipment.tripDate,
    })
    const planRoom = Math.max(0, planQty - used)
    if (totalStock >= planRoom) {
      return planRoom
    }
    return Math.min(planRoom, Math.max(0, totalStock))
  }

  function getStockLimitForLine(line: ShipmentLine): number {
    if (!selectedShipmentId) return 0
    const productCap = productDispatchCap(line.productName, line.id)
    if (productCap !== null) return productCap
    return getLineStockLimit(
      shipments,
      selectedShipmentId,
      line.productName,
      line.productionDate,
      stockItems,
      useApi,
    )
  }

  function isStockSufficientForPlan(productName: string): boolean {
    if (!outletPlan) return false
    const planQty = outletPlan[productName] ?? 0
    if (planQty <= 0) return false
    return totalAvailableForProduct(productName, stockItems, remainingStock) >= planQty
  }

  function openCreateModal() {
    setSelectedOutlets([])
    setCreateTripDate('today')
    setNewDelman(delmanOptions[0] ?? '')
    setCreateError(null)
    setIsCreateOpen(true)
  }

  function toggleOutletSelection(outlet: string) {
    if (!outletHasHoPlan(outlet, outletPlansByName)) return
    setSelectedOutlets((prev) =>
      prev.includes(outlet) ? prev.filter((name) => name !== outlet) : [...prev, outlet],
    )
    setCreateError(null)
  }

  function plannedProductsForSelectedOutlets(): string[] {
    const names = new Set<string>()
    for (const outlet of selectedOutlets) {
      for (const line of outletPlansByName[outlet] ?? []) {
        if (line.productName.trim()) names.add(line.productName.trim())
      }
    }
    return [...names]
  }

  function requestGenerateShipments() {
    if (createTripDate === 'today') {
      const conflicts = findDadakanConflicts(shipments, plannedProductsForSelectedOutlets())
      if (conflicts.length > 0) {
        setDadakanWarning({ action: 'generate', conflicts })
        return
      }
    }
    void generateShipments()
  }

  function requestOpenHandoverPin() {
    if (selectedShipment && isTripDateToday(selectedShipment.tripDate)) {
      const products = getFilledLines(selectedShipment.lines).map((l) => l.productName)
      const conflicts = findDadakanConflicts(shipments, products)
      if (conflicts.length > 0) {
        setDadakanWarning({ action: 'open-pin', conflicts })
        return
      }
    }
    setIsPinModalOpen(true)
  }

  function continueAfterDadakanWarning() {
    const action = dadakanWarning?.action
    setDadakanWarning(null)
    if (action === 'generate') void generateShipments()
    if (action === 'open-pin') setIsPinModalOpen(true)
  }

  async function generateShipments() {
    if (hasNoKitchenStock) {
      setCreateError(kitchenTerms.dispatch.create.errors.noStock)
      return
    }
    if (selectedOutlets.length === 0 || !newDelman) {
      setCreateError(
        selectableOutlets.length === 0
          ? kitchenTerms.dispatch.create.errors.noSelectableOutlets
          : kitchenTerms.dispatch.create.errors.selectOutletDelman,
      )
      return
    }
    if (!useApi || !device?.deviceId) {
      setCreateError(kitchenTerms.dispatch.create.errors.needsApi)
      return
    }

    setIsGenerating(true)
    setCreateError(null)
    try {
      const tripDate =
        createTripDate === 'tomorrow' ? kitchenTomorrowISO() : kitchenTodayISO()
      const result = await generateDispatchDeliveries({
        deviceId: device.deviceId,
        outletNames: selectedOutlets,
        delmanName: newDelman,
        tripDate,
      })
      await reloadDeliveries()
      setIsCreateOpen(false)
      setListFilter('pending')
      setCreateError(null)
      setGenerateResult(result)
      setGenerateResultOutletCount(selectedOutlets.length)
      setGenerateResultTripLabel(createTripDate === 'tomorrow' ? 'Besok' : 'Hari ini')
    } catch (err) {
      const message = err instanceof Error ? err.message : kitchenTerms.dispatch.create.errors.generateFailed
      setCreateError(message)
    } finally {
      setIsGenerating(false)
    }
  }

  function openShipment(id: string) {
    setSelectedShipmentId(id)
    setEditorStep('edit')
    setView('edit')
    setSaveError(null)
  }

  function closeShipment() {
    setSelectedShipmentId(null)
    setEditorStep('edit')
    setView('list')
    setSaveError(null)
  }

  function updateLine(lineId: string, qty: number) {
    if (!selectedShipmentId) return
    setShipments((prev) =>
      prev.map((s) => {
        if (s.id !== selectedShipmentId) return s
        return {
          ...s,
          lines: s.lines.map((line) =>
            line.id === lineId ? { ...line, qty: Math.max(0, qty) } : line,
          ),
        }
      }),
    )
  }

  function removeLine(lineId: string) {
    if (!selectedShipmentId) return
    setShipments((prev) =>
      prev.map((s) =>
        s.id === selectedShipmentId
          ? { ...s, lines: s.lines.filter((line) => line.id !== lineId) }
          : s,
      ),
    )
  }

  function canDeleteShipment(shipment: Shipment): boolean {
    return shipment.status === 'draft' && getFilledLines(shipment.lines).length === 0
  }

  function deleteShipment(id: string) {
    const shipment = shipments.find((s) => s.id === id)
    if (!shipment || !canDeleteShipment(shipment)) return
    setShipments((prev) => prev.filter((s) => s.id !== id))
    if (selectedShipmentId === id) closeShipment()
    showToast(kitchenTerms.dispatch.toast.deleted)
  }

  function validateLines(lines: ShipmentLine[]): boolean {
    const filled = getFilledLines(lines)
    if (filled.length === 0) {
      setSaveError(kitchenTerms.dispatch.errors.saveMinOne)
      return false
    }

    const qtyByProduct = new Map<string, number>()
    for (const line of filled) {
      qtyByProduct.set(line.productName, (qtyByProduct.get(line.productName) ?? 0) + line.qty)
    }
    if (selectedShipment && outletPlan) {
      for (const [productName, totalQty] of qtyByProduct) {
        const planQty = outletPlan[productName] ?? 0
        const usedOther = qtyUsedForOutletProduct(
          shipments,
          selectedShipment.storeName,
          productName,
          {
            excludeShipmentId: selectedShipmentId,
            tripDate: selectedShipment.tripDate,
          },
        )
        // remainingStock sudah netto kiriman lain (draft/prepared); jangan kurangi usedOther lagi.
        const totalStock = totalAvailableForProduct(productName, stockItems, remainingStock)
        const planRoom = Math.max(0, planQty - usedOther)
        if (totalQty > planRoom + 1e-9) {
          setSaveError(kitchenTerms.dispatch.errors.overPlan(productName, planQty))
          return false
        }
        if (totalQty > totalStock + 1e-9) {
          setSaveError(
            kitchenTerms.dispatch.errors.overStock(productName, Math.max(0, totalStock)),
          )
          return false
        }
      }
    }

    const overStock = filled.find((line) => line.qty > getStockLimitForLine(line))
    if (overStock) {
      const limit = getStockLimitForLine(overStock)
      setSaveError(kitchenTerms.dispatch.errors.overStock(overStock.productName, limit))
      return false
    }
    setSaveError(null)
    return true
  }

  async function persistShipmentToServer(shipment: Shipment): Promise<Shipment> {
    if (!useApi || !device?.deviceId) return shipment

    const filled = getFilledLines(shipment.lines)
    const result = await saveDispatchDelivery({
      deliveryId: shipment.id,
      deviceId: device.deviceId,
      storeName: shipment.storeName,
      delmanName: shipment.delmanName,
      lines: filled.map((line) => ({
        productName: line.productName,
        productionDate: line.productionDate,
        qty: line.qty,
      })),
    })

    return {
      ...shipment,
      id: result.delivery_code || shipment.id,
      deliveryId: result.delivery_id,
      status: 'prepared',
      updatedAt: result.updated_at,
    }
  }

  async function saveShipmentDraft() {
    if (!selectedShipmentId || !selectedShipment) return
    if (!validateLines(selectedShipment.lines)) return

    setIsSavingShipment(true)
    try {
      let saved = selectedShipment
      if (useApi) {
        saved = await persistShipmentToServer(selectedShipment)
      } else {
        saved = {
          ...selectedShipment,
          status: 'prepared',
          updatedAt: formatSavedAt(),
        }
      }

      setShipments((prev) =>
        prev.map((s) => (s.id === selectedShipmentId ? saved : s)),
      )
      setSaveError(null)
      closeShipment()
      setListFilter('pending')
      showToast(kitchenTerms.dispatch.toast.saved(consumeLastSyncQueued()))
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : kitchenTerms.dispatch.errors.saveFailed)
    } finally {
      setIsSavingShipment(false)
    }
  }

  async function openHandoverReview() {
    if (!selectedShipmentId || !selectedShipment) return
    if (!validateLines(selectedShipment.lines)) return

    setIsSavingShipment(true)
    try {
      let saved = selectedShipment
      if (useApi) {
        saved = await persistShipmentToServer(selectedShipment)
        setShipments((prev) =>
          prev.map((s) => (s.id === selectedShipmentId ? saved : s)),
        )
      } else {
        setShipments((prev) =>
          prev.map((s) =>
            s.id === selectedShipmentId
              ? { ...s, status: 'prepared', updatedAt: formatSavedAt() }
              : s,
          ),
        )
      }
      setEditorStep('review')
      setSaveError(null)
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : kitchenTerms.dispatch.errors.saveFailed)
    } finally {
      setIsSavingShipment(false)
    }
  }

  async function confirmHandoverToDelman(pin: string) {
    if (!selectedShipment || !isEditableStatus(selectedShipment.status)) return
    if (!validateLines(selectedShipment.lines)) return

    setIsDispatching(true)
    try {
      const filled = getFilledLines(selectedShipment.lines)
      const result = await dispatchDeliveryToDelman({
        deliveryId: selectedShipment.id,
        deviceId: device?.deviceId ?? '',
        pin,
        storeName: selectedShipment.storeName,
        delmanName: selectedShipment.delmanName,
        lines: filled.map((line) => ({
          productName: line.productName,
          productionDate: line.productionDate,
          qty: line.qty,
        })),
      })

      const handoverLabel = formatHandoverTime(result.dispatched_at)
      const totalQty = filled.reduce((sum, line) => sum + line.qty, 0)

      setShipments((prev) =>
        prev.map((s) =>
          s.id === selectedShipment.id
            ? { ...s, status: 'in_transit' as const, updatedAt: handoverLabel }
            : s,
        ),
      )

      setIsPinModalOpen(false)
      showToast(
        kitchenTerms.dispatch.toast.handover(
          totalQty,
          selectedShipment.storeName,
          selectedShipment.delmanName,
          consumeLastSyncQueued(),
        ),
      )
      closeShipment()
      setListFilter('in_transit')
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : kitchenTerms.dispatch.errors.handoverFailed)
      throw err
    } finally {
      setIsDispatching(false)
    }
  }

  function renderDispatchLineEditor(line: ShipmentLine) {
    const stockLimit = getStockLimitForLine(line)
    const overStock = isEditable && line.qty > stockLimit
    const planQty = outletPlan?.[line.productName] ?? 0
    const stockCoversPlan = isEditable && isStockSufficientForPlan(line.productName) && planQty > 0
    return (
      <div key={line.id} className="rounded-lg border border-outline-variant p-3 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-body-lg font-semibold text-on-surface">{line.productName}</p>
            <p className="text-label-md text-on-surface-variant">
              {kitchenTerms.dispatch.editor.productionDate(line.productionDate)}
              {isEditable &&
                (stockCoversPlan
                  ? kitchenTerms.dispatch.editor.maxShipPlan(planQty)
                  : kitchenTerms.dispatch.editor.maxShip(stockLimit))}
            </p>
            {isEditable && outletPlan && selectedShipment && (
              <p className="text-label-md text-on-surface-variant">
                {kitchenTerms.dispatch.editor.planToStore(
                  selectedShipment.storeName,
                  outletPlan[line.productName] ?? 0,
                )}
                {' · '}
                {kitchenTerms.dispatch.editor.planRemaining(
                  planRemainingForProduct(line.productName, line.id) ?? 0,
                )}
              </p>
            )}
          </div>
          {isEditable && (
            <button
              type="button"
              onClick={() => removeLine(line.id)}
              className="text-status-danger"
              aria-label={kitchenTerms.dispatch.editor.removeProductAria}
            >
              <span className="material-symbols-outlined">delete</span>
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <label className="text-body-sm text-on-surface-variant">
            {kitchenTerms.dispatch.editor.qtyLabel}
          </label>
          <QtyStepper
            value={line.qty}
            min={0}
            max={isEditable ? stockLimit : undefined}
            onChange={(qty) => updateLine(line.id, qty)}
            disabled={!isEditable}
          />
          <span className="text-body-sm text-on-surface-variant">{kitchenTerms.production.pcs}</span>
        </div>
        {overStock && (
          <p className="text-label-md text-status-danger font-medium">
            {kitchenTerms.dispatch.editor.overMax(stockLimit)}
          </p>
        )}
      </div>
    )
  }

  function renderShipmentCard(shipment: Shipment) {
    const totalQty = getFilledLines(shipment.lines).reduce((sum, line) => sum + line.qty, 0)
    const deletable = canDeleteShipment(shipment)
    const needsAction = isActiveAtKitchen(shipment.status)
    const isReadyHandover = shipment.status === 'prepared'

    return (
      <div
        key={shipment.id}
        className={`card ${isReadyHandover ? 'border-status-warning/40 bg-status-warning-bg/30' : ''}`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className="font-label-md font-mono text-on-surface-variant">{shipment.id}</p>
            <p className="text-body-lg font-bold text-on-surface mt-1">{shipment.storeName}</p>
            <p className="text-body-sm text-on-surface-variant mt-1">
              {kitchenTerms.dispatch.card.delmanPrefix} {shipment.delmanName}
            </p>
            <p className="text-body-sm text-on-surface-variant mt-0.5">
              {kitchenTerms.dispatch.create.tripDateLabel}:{' '}
              <span className="font-semibold text-on-surface">
                {formatTripDateLabel(shipment.tripDate ?? kitchenTodayISO())}
              </span>
            </p>
            <p className="text-body-sm text-on-surface-variant mt-0.5">
              {totalQty > 0
                ? kitchenTerms.dispatch.card.productsSummary(
                    getFilledLines(shipment.lines).length,
                    totalQty,
                  )
                : kitchenTerms.dispatch.card.emptyProducts}
            </p>
            <p className="text-label-md text-on-surface-variant mt-2">{shipment.updatedAt}</p>
          </div>
          <div className="flex flex-col items-end gap-2 shrink-0">
            <span className={shipmentStatusBadge(shipment.status)}>
              {shipmentStatusShortLabel(shipment.status)}
            </span>
            {deletable && (
              <button
                type="button"
                onClick={() => deleteShipment(shipment.id)}
                className="text-status-danger p-1"
                aria-label={kitchenTerms.dispatch.card.deleteAria}
              >
                <span className="material-symbols-outlined text-xl">delete</span>
              </button>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={() => openShipment(shipment.id)}
          className={`mt-3 w-full min-h-[44px] ${needsAction ? 'btn-primary' : 'btn-outline'}`}
        >
          {isReadyHandover
            ? kitchenTerms.dispatch.card.handover
            : needsAction
              ? kitchenTerms.dispatch.card.arrange
              : kitchenTerms.dispatch.card.viewDetail}
        </button>
      </div>
    )
  }

  function renderGroupedShipments(shipments: Shipment[]) {
    if (shipments.length === 0) return null

    return groupShipmentsByDelman(shipments).map((group) => (
      <div key={group.delmanName} className="space-y-2">
        <div className="flex items-center gap-2 px-1 pt-1">
          <span className="material-symbols-outlined text-primary text-lg">local_shipping</span>
          <p className="text-body-sm font-semibold text-on-surface">{group.delmanName}</p>
          <span className="badge-draft">{kitchenTerms.dispatch.card.storesBadge(group.shipments.length)}</span>
        </div>
        {group.shipments.map((shipment) => renderShipmentCard(shipment))}
      </div>
    ))
  }

  const filledLines = selectedShipment ? getFilledLines(selectedShipment.lines) : []
  const filledTotalQty = filledLines.reduce((sum, line) => sum + line.qty, 0)
  const isEditable = selectedShipment ? isEditableStatus(selectedShipment.status) : false

  const shipmentLineGroups = useMemo(() => {
    if (!selectedShipment) return []
    return groupProductLinesByCategory(selectedShipment.lines, planCatalog)
  }, [selectedShipment, planCatalog])

  const filledLineGroups = useMemo(() => {
    if (!selectedShipment) return []
    return groupProductLinesByCategory(getFilledLines(selectedShipment.lines), planCatalog)
  }, [selectedShipment, planCatalog])

  const listEmptyConfig: Record<ListFilter, { icon: string; title: string; description: string }> = {
    pending: {
      icon: 'local_shipping',
      title: kitchenTerms.dispatch.empty.pending.title,
      description: kitchenTerms.dispatch.empty.pending.description,
    },
    in_transit: {
      icon: 'delivery_truck_speed',
      title: kitchenTerms.dispatch.empty.inTransit.title,
      description: kitchenTerms.dispatch.empty.inTransit.description,
    },
    received: {
      icon: 'storefront',
      title: kitchenTerms.dispatch.empty.received.title,
      description: kitchenTerms.dispatch.empty.received.description,
    },
  }

  return (
    <section className="space-y-4">
      <div className="card">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-headline-md text-primary">{kitchenTerms.dispatch.title}</p>
            <p className="text-body-sm text-on-surface-variant mt-1 capitalize">
              {kitchenName} · {today}
            </p>
          </div>
          {useApi && (
            <button
              type="button"
              className="btn-outline min-h-[44px] shrink-0 inline-flex items-center gap-2"
              onClick={handleDownloadDispatchPdf}
              disabled={isDownloadingPdf}
            >
              <span className="material-symbols-outlined text-lg">download</span>
              {isDownloadingPdf
                ? kitchenTerms.dispatch.downloadingPdf
                : kitchenTerms.dispatch.downloadPdf}
            </button>
          )}
        </div>
        <p className="text-body-sm text-on-surface-variant mt-3 pt-3 border-t border-surface-variant">
          {kitchenTerms.dispatch.subtitleHint}
        </p>
      </div>

      {view === 'list' && hasNoKitchenStock && (
        <div className="card border border-status-warning/40 bg-status-warning-bg/30 space-y-3">
          <div className="flex items-start gap-3">
            <span className="material-symbols-outlined text-status-warning text-2xl shrink-0">inventory_2</span>
            <div className="min-w-0 flex-1 space-y-1">
              <p className="text-body-sm font-semibold text-on-surface">
                {kitchenTerms.dispatch.zeroStock.bannerTitle}
              </p>
              <p className="text-body-sm text-on-surface-variant">
                {kitchenTerms.dispatch.zeroStock.bannerBody}
              </p>
            </div>
          </div>
          <Link href="/kitchen/production" className="btn-primary w-full text-center min-h-[44px]">
            {kitchenTerms.dispatch.zeroStock.goProduction}
          </Link>
        </div>
      )}

      {view === 'list' && (
        <div className="space-y-3">
          {apiLoadError && (
            <div role="alert" className="alert-error flex flex-wrap items-center justify-between gap-2">
              <span className="text-body-sm">{apiLoadError}</span>
              <button
                type="button"
                className="btn-outline min-h-[36px] text-body-sm"
                onClick={() => {
                  setIsLoading(true)
                  void reloadDeliveries().finally(() => setIsLoading(false))
                }}
              >
                {kitchenTerms.dispatch.retry}
              </button>
            </div>
          )}
          {isLoading && <SkeletonList count={3} label={kitchenTerms.dispatch.loading} />}
          <div className="card space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-body-sm text-on-surface-variant">{kitchenTerms.dispatch.todayList}</p>
              <div className="flex flex-col items-end gap-1">
                <button
                  type="button"
                  className="btn-primary"
                  onClick={openCreateModal}
                  disabled={hasNoKitchenStock}
                >
                  + {kitchenTerms.dispatch.generate}
                </button>
                {hasNoKitchenStock && (
                  <p className="text-label-md text-on-surface-variant text-right max-w-[12rem]">
                    {kitchenTerms.dispatch.zeroStock.generateDisabledHint}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2">
              {[
                {
                  key: 'pending' as const,
                  label: kitchenTerms.dispatch.filters.pending,
                  count: pendingShipments.length,
                },
                {
                  key: 'in_transit' as const,
                  label: kitchenTerms.dispatch.filters.inTransit,
                  count: inTransitShipments.length,
                },
                {
                  key: 'received' as const,
                  label: kitchenTerms.dispatch.filters.received,
                  count: receivedShipments.length,
                },
              ].map((filter) => (
                <button
                  key={filter.key}
                  type="button"
                  onClick={() => setListFilter(filter.key)}
                  className={`rounded-lg px-2 py-2 text-body-sm font-semibold leading-tight ${
                    listFilter === filter.key
                      ? 'bg-primary-container text-on-primary-container'
                      : 'bg-surface text-on-surface-variant border border-outline-variant'
                  }`}
                >
                  {filter.label}
                  {filter.count > 0 && <span className="block text-xs opacity-80 mt-0.5">({filter.count})</span>}
                </button>
              ))}
            </div>
          </div>

          {listFilter === 'pending' ? (
            <>
              {preparedPending.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between px-1">
                    <p className="text-body-sm font-bold text-status-warning">
                      {kitchenTerms.dispatch.sections.readyHandover}
                    </p>
                    <span className="badge-warning">{preparedPending.length}</span>
                  </div>
                  {renderGroupedShipments(preparedPending)}
                </div>
              )}
              {draftPending.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between px-1">
                    <p className="text-body-sm font-bold text-on-surface-variant">
                      {kitchenTerms.dispatch.sections.needsWork}
                    </p>
                    <span className="badge-draft">{draftPending.length}</span>
                  </div>
                  {renderGroupedShipments(draftPending)}
                </div>
              )}
            </>
          ) : (
            renderGroupedShipments(visibleShipments)
          )}

          {!isLoading && visibleShipments.length === 0 && (
            <div className="card">
              <EmptyState
                icon={listEmptyConfig[listFilter].icon}
                title={listEmptyConfig[listFilter].title}
                description={listEmptyConfig[listFilter].description}
              />
              {listFilter === 'pending' && (
                <div className="space-y-3">
                  {!hasNoKitchenStock && (
                    <button
                      type="button"
                      className="btn-primary w-full"
                      onClick={openCreateModal}
                    >
                      + {kitchenTerms.dispatch.generate}
                    </button>
                  )}
                  {hasNoKitchenStock && (
                    <Link href="/kitchen/production" className="btn-primary w-full text-center block">
                      {kitchenTerms.dispatch.zeroStock.goProduction}
                    </Link>
                  )}
                </div>
              )}
            </div>
          )}

          <Link href="/kitchen/home" className="btn-outline block text-center">
            {kitchenTerms.dispatch.backHome}
          </Link>
        </div>
      )}

      {view === 'edit' && selectedShipment && editorStep === 'edit' && (
        <div className={isEditable ? 'relative pb-28' : undefined}>
          <div className="card space-y-3">
            <div className="flex items-center justify-between gap-2 border-b border-surface-variant pb-3">
              <div>
                <p className="font-label-md font-mono text-on-surface-variant">{selectedShipment.id}</p>
                <p className="text-headline-md text-primary mt-1">{selectedShipment.storeName}</p>
                <p className="text-body-sm text-on-surface-variant">
                  {kitchenTerms.dispatch.card.delmanPrefix} {selectedShipment.delmanName} ·{' '}
                  {shipmentStatusLabel(selectedShipment.status)}
                </p>
              </div>
              <button type="button" className="btn-outline min-h-[44px]" onClick={closeShipment}>
                {kitchenTerms.dispatch.back}
              </button>
            </div>

            <FlowStepper
              steps={[...DISPATCH_FLOW_STEPS]}
              activeStep={dispatchFlowActiveStep(selectedShipment.status, editorStep)}
            />

            {isEditable && visibleStockChips.length > 0 && (
              <div className="rounded-lg bg-surface-container p-3 space-y-2">
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-2 text-left"
                  onClick={() => setIsStockOpen((v) => !v)}
                  aria-expanded={isStockOpen}
                >
                  <span className="text-label-md font-semibold text-on-surface-variant">
                    {kitchenTerms.dispatch.editor.stockAvailable}
                    <span className="ml-1 text-on-surface-variant/80">
                      {kitchenTerms.dispatch.editor.stockProducts(visibleStockChips.length)}
                    </span>
                  </span>
                  <span className="material-symbols-outlined text-on-surface-variant">
                    {isStockOpen ? 'expand_less' : 'expand_more'}
                  </span>
                </button>
                {isStockOpen && (
                  <>
                    <div className="flex max-h-44 flex-wrap gap-2 overflow-y-auto">
                      {visibleStockChips.map((item) => (
                        <span
                          key={item.key}
                          className="text-body-sm text-on-surface bg-surface-card border border-outline-variant rounded-lg px-2.5 py-1.5"
                        >
                          {item.productName}{' '}
                          <span className="text-on-surface-variant">({item.productionDate})</span>{' '}
                          <strong>{item.available} pcs</strong>
                        </span>
                      ))}
                    </div>
                    <p className="text-label-md text-on-surface-variant">
                      {kitchenTerms.dispatch.editor.stockHint}
                    </p>
                  </>
                )}
              </div>
            )}

            <div className="rounded-lg bg-surface-container p-3 text-body-sm space-y-1">
              <p>
                <span className="text-on-surface-variant">{kitchenTerms.dispatch.editor.storeLabel}</span>{' '}
                <strong className="text-on-surface">{selectedShipment.storeName}</strong>
              </p>
              <p>
                <span className="text-on-surface-variant">{kitchenTerms.dispatch.editor.delmanLabel}</span>{' '}
                <strong className="text-on-surface">{selectedShipment.delmanName}</strong>
              </p>
              {isEditable && (
                <p className="text-label-md text-on-surface-variant pt-1">
                  {kitchenTerms.dispatch.editor.storeDelmanFixed}
                </p>
              )}
            </div>

          {selectedShipment.lines.length === 0 && isEditable && (
            <div className="rounded-lg border border-dashed border-outline-variant p-4 text-center space-y-2">
              {hasNoKitchenStock ? (
                <>
                  <p className="text-body-sm font-semibold text-on-surface">
                    {kitchenTerms.dispatch.zeroStock.editorEmptyTitle}
                  </p>
                  <p className="text-body-sm text-on-surface-variant">
                    {kitchenTerms.dispatch.zeroStock.editorEmptyBody}
                  </p>
                  <Link href="/kitchen/production" className="btn-outline inline-block min-h-[44px] mt-2">
                    {kitchenTerms.dispatch.zeroStock.goProduction}
                  </Link>
                </>
              ) : (
                <p className="text-body-sm text-on-surface-variant">
                  {kitchenTerms.dispatch.editor.noProducts}
                </p>
              )}
            </div>
          )}

          <div className="space-y-2">
            {shipmentLineGroups.map((group) => {
              const groupQty = group.items.reduce((sum, line) => sum + line.qty, 0)
              return (
                <CollapsibleCategoryBlock
                  key={group.categoryName}
                  categoryName={group.categoryName}
                  itemCount={group.items.length}
                  defaultOpen={false}
                  summary={`${groupQty} ${kitchenTerms.production.pcs}`}
                >
                  <div className="space-y-2 p-2">
                    {group.items.map((line) => renderDispatchLineEditor(line))}
                  </div>
                </CollapsibleCategoryBlock>
              )
            })}
          </div>

          {filledLines.length > 0 && (
            <div className="rounded-lg bg-surface-container p-3 flex items-center justify-between">
              <span className="text-body-sm text-on-surface-variant">
                {kitchenTerms.dispatch.editor.totalShip}
              </span>
              <span className="text-body-lg font-bold text-on-surface">
                {kitchenTerms.dispatch.card.productsSummary(filledLines.length, filledTotalQty)}
              </span>
            </div>
          )}

          {saveError && (
            <div role="alert" className="alert-error">
              <span className="material-symbols-outlined text-base shrink-0">error</span>
              <span>{saveError}</span>
            </div>
          )}

          {selectedShipment.status === 'received' && (
            <p className="text-body-sm text-on-surface-variant text-center">
              {kitchenTerms.dispatch.editor.receivedReadonly}
            </p>
          )}
          </div>

          {isEditable && (
            <StickyActionFooter>
              <button
                type="button"
                className="btn-outline w-full min-h-[44px]"
                onClick={saveShipmentDraft}
                disabled={isSavingShipment || isDispatching}
              >
                {isSavingShipment
                  ? kitchenTerms.dispatch.editor.saving
                  : kitchenTerms.dispatch.editor.save}
              </button>
              <button
                type="button"
                className="btn-primary w-full min-h-[44px]"
                onClick={openHandoverReview}
                disabled={isSavingShipment || isDispatching}
              >
                {kitchenTerms.dispatch.editor.handover}
              </button>
              <p className="text-label-md text-on-surface-variant text-center">
                {kitchenTerms.dispatch.editor.footerHint}
              </p>
            </StickyActionFooter>
          )}
        </div>
      )}

      {view === 'edit' && selectedShipment && editorStep === 'review' && (
        <div className="relative pb-28">
          <div className="card space-y-3">
            <div className="flex items-center justify-between gap-2 border-b border-surface-variant pb-3">
              <div>
                <p className="font-label-md font-mono text-on-surface-variant">{selectedShipment.id}</p>
                <p className="text-headline-md text-primary mt-1">{kitchenTerms.dispatch.review.title}</p>
                <p className="text-body-sm text-on-surface-variant">{selectedShipment.storeName}</p>
              </div>
              <button
                type="button"
                className="btn-outline min-h-[44px]"
                onClick={() => setEditorStep('edit')}
              >
                {kitchenTerms.dispatch.back}
              </button>
            </div>

            <FlowStepper
              steps={[...DISPATCH_FLOW_STEPS]}
              activeStep={dispatchFlowActiveStep(selectedShipment.status, editorStep)}
            />

            <p className="text-body-sm text-on-surface-variant">
              {kitchenTerms.dispatch.review.hint}
            </p>

            <div className="rounded-lg bg-surface-container p-3 text-body-sm space-y-1">
              <p>
                <span className="text-on-surface-variant">{kitchenTerms.dispatch.editor.storeLabel}</span>{' '}
                <strong className="text-on-surface">{selectedShipment.storeName}</strong>
              </p>
              <p>
                <span className="text-on-surface-variant">{kitchenTerms.dispatch.editor.delmanLabel}</span>{' '}
                <strong className="text-on-surface">{selectedShipment.delmanName}</strong>
              </p>
            </div>

            <div className="space-y-2">
              {filledLineGroups.map((group) => {
                const groupQty = group.items.reduce((sum, line) => sum + line.qty, 0)
                return (
                  <CollapsibleCategoryBlock
                    key={group.categoryName}
                    categoryName={group.categoryName}
                    itemCount={group.items.length}
                    defaultOpen
                    summary={`${groupQty} ${kitchenTerms.production.pcs}`}
                  >
                    <div className="p-2">
                      <ActionSummaryCard
                        items={group.items.map((line) => ({
                          id: line.id,
                          title: line.productName,
                          subtitle: kitchenTerms.dispatch.review.productionDate(line.productionDate),
                          value: `${line.qty} ${kitchenTerms.production.pcs}`,
                        }))}
                        totalLabel={kitchenTerms.dispatch.review.totalLabel}
                        totalValue={`${group.items.length} produk · ${groupQty} ${kitchenTerms.production.pcs}`}
                      />
                    </div>
                  </CollapsibleCategoryBlock>
                )
              })}
            </div>

            <div className="rounded-lg bg-surface-container p-3 flex items-center justify-between">
              <span className="text-body-sm text-on-surface-variant">
                {kitchenTerms.dispatch.editor.totalShip}
              </span>
              <span className="text-body-lg font-bold text-on-surface">
                {kitchenTerms.dispatch.card.productsSummary(filledLines.length, filledTotalQty)}
              </span>
            </div>

            {saveError && (
              <div role="alert" className="alert-error">
                <span className="material-symbols-outlined text-base shrink-0">error</span>
                <span>{saveError}</span>
              </div>
            )}
          </div>

          <StickyActionFooter>
            <button
              type="button"
              className="btn-primary w-full min-h-[44px]"
              onClick={requestOpenHandoverPin}
              disabled={isDispatching}
            >
              {kitchenTerms.dispatch.review.confirmPin}
            </button>
          </StickyActionFooter>
        </div>
      )}

      {isCreateOpen && (
        <div className="fixed inset-0 z-30 bg-inverse-surface/40 flex items-center justify-center p-4">
          <div className="w-full max-w-md max-h-[90vh] flex flex-col bg-surface-card border border-outline-variant rounded-xl shadow-sm">
            <div className="px-4 py-3 border-b border-surface-variant flex items-center justify-between shrink-0">
              <p className="text-headline-md text-primary">{kitchenTerms.dispatch.create.title}</p>
              <button
                type="button"
                onClick={() => setIsCreateOpen(false)}
                className="text-on-surface-variant"
                aria-label={kitchenTerms.dispatch.create.closeAria}
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="p-4 space-y-4 overflow-y-auto flex-1">
              {hasNoKitchenStock ? (
                <div className="space-y-4">
                  <div className="rounded-lg border border-status-warning/40 bg-status-warning-bg/30 p-4 space-y-2">
                    <div className="flex items-start gap-3">
                      <span className="material-symbols-outlined text-status-warning text-2xl shrink-0">
                        factory
                      </span>
                      <div className="space-y-1">
                        <p className="text-body-sm font-semibold text-on-surface">
                          {kitchenTerms.dispatch.zeroStock.modalTitle}
                        </p>
                        <p className="text-body-sm text-on-surface-variant">
                          {kitchenTerms.dispatch.zeroStock.modalBody}
                        </p>
                      </div>
                    </div>
                    <p className="text-label-md text-on-surface-variant">
                      {kitchenTerms.dispatch.zeroStock.stockSummaryZero(
                        createModalPlanPcs > 0 ? createModalPlanPcs : hoPlanTotalPcs,
                        createModalPlanProducts,
                      )}
                    </p>
                  </div>
                  <Link
                    href="/kitchen/production"
                    className="btn-primary w-full text-center block min-h-[44px]"
                    onClick={() => setIsCreateOpen(false)}
                  >
                    {kitchenTerms.dispatch.zeroStock.goProduction}
                  </Link>
                </div>
              ) : (
                <>
              <p className="text-body-sm text-on-surface-variant">
                {kitchenTerms.dispatch.create.hint}
              </p>
              <div className="space-y-2">
                <p className="field-label">{kitchenTerms.dispatch.create.tripDateLabel}</p>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setCreateTripDate('today')}
                    className={`rounded-lg px-3 py-2.5 text-body-sm font-semibold min-h-[44px] ${
                      createTripDate === 'today'
                        ? 'bg-primary-container text-on-primary-container'
                        : 'bg-surface text-on-surface-variant border border-outline-variant'
                    }`}
                  >
                    {kitchenTerms.dispatch.create.tripDateToday}
                  </button>
                  <button
                    type="button"
                    disabled={!tomorrowPlanAvailable}
                    onClick={() => setCreateTripDate('tomorrow')}
                    className={`rounded-lg px-3 py-2.5 text-body-sm font-semibold min-h-[44px] ${
                      !tomorrowPlanAvailable
                        ? 'bg-surface-container-low text-on-surface-variant/60 border border-outline-variant/60 opacity-60 cursor-not-allowed'
                        : createTripDate === 'tomorrow'
                          ? 'bg-primary-container text-on-primary-container'
                          : 'bg-surface text-on-surface-variant border border-outline-variant'
                    }`}
                  >
                    {kitchenTerms.dispatch.create.tripDateTomorrow}
                  </button>
                </div>
                {!tomorrowPlanAvailable && (
                  <p className="text-body-sm text-on-surface-variant">
                    {kitchenTerms.dispatch.create.tripDateTomorrowDisabledHint}
                  </p>
                )}
              </div>
              {stockLoaded && totalKitchenStock > 0 && createModalPlanPcs > 0 && (
                <p className="text-body-sm text-on-surface-variant rounded-lg bg-surface-container px-3 py-2">
                  {kitchenTerms.dispatch.zeroStock.stockSummaryPartial(
                    totalKitchenStock,
                    createModalPlanPcs,
                  )}
                </p>
              )}
              <div className="space-y-2">
                <p className="field-label">{kitchenTerms.dispatch.create.outletsLabel}</p>
                {outletOptions.length === 0 ? (
                  <p className="text-body-sm text-on-surface-variant rounded-lg border border-dashed border-outline-variant p-3">
                    {kitchenTerms.dispatch.create.noOutlets}
                  </p>
                ) : isLoadingCreateOutletPlans ? (
                  <p className="text-body-sm text-on-surface-variant">
                    {kitchenTerms.dispatch.create.loadingPlans}
                  </p>
                ) : (
                  <div className="space-y-2 max-h-52 overflow-y-auto">
                    {outletOptions.map((outlet) => {
                      const checked = selectedOutlets.includes(outlet)
                      const planLines = outletPlansByName[outlet] ?? []
                      const hasPlan = planLines.length > 0
                      return (
                        <button
                          key={outlet}
                          type="button"
                          onClick={() => toggleOutletSelection(outlet)}
                          disabled={!hasPlan}
                          className={`w-full text-left rounded-lg border p-3 min-h-[44px] transition-colors ${
                            !hasPlan
                              ? 'border-outline-variant/60 bg-surface-container-low opacity-60 cursor-not-allowed'
                              : checked
                                ? 'border-primary bg-primary-container/20'
                                : 'border-outline-variant bg-surface-container-low'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <span
                              className={`material-symbols-outlined text-lg mt-0.5 ${
                                !hasPlan
                                  ? 'text-on-surface-variant/60'
                                  : checked
                                    ? 'text-primary'
                                    : 'text-on-surface-variant'
                              }`}
                            >
                              {!hasPlan
                                ? 'block'
                                : checked
                                  ? 'check_box'
                                  : 'check_box_outline_blank'}
                            </span>
                            <div className="min-w-0 flex-1">
                              <p className="text-body-sm font-semibold text-on-surface">{outlet}</p>
                              <p className="text-label-md text-on-surface-variant mt-0.5">
                                {hasPlan
                                  ? kitchenTerms.dispatch.create.hoPlanPcs(sumPlanLines(planLines))
                                  : kitchenTerms.dispatch.create.noHoPlan}
                              </p>
                            </div>
                          </div>
                        </button>
                      )
                    })}
                  </div>
                )}
                {selectedOutlets.length > 0 && !isLoadingCreateOutletPlans && (
                  <p className="text-label-md text-on-surface-variant">
                    {kitchenTerms.dispatch.create.selectedSummary(
                      selectedOutlets.length,
                      selectedCreatePlanTotal,
                    )}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <p className="field-label">{kitchenTerms.dispatch.create.delmanLabel}</p>
                {delmanOptions.length === 0 ? (
                  <p className="text-body-sm text-on-surface-variant rounded-lg border border-dashed border-outline-variant p-3">
                    {kitchenTerms.dispatch.create.noDelmans}
                  </p>
                ) : (
                  <div className="grid grid-cols-1 gap-2">
                    {delmanOptions.map((delman) => (
                      <button
                        key={delman}
                        type="button"
                        onClick={() => setNewDelman(delman)}
                        className={`rounded-lg px-3 py-2.5 text-body-sm font-semibold min-h-[44px] text-left ${
                          newDelman === delman
                            ? 'bg-primary-container text-on-primary-container'
                            : 'bg-surface text-on-surface-variant border border-outline-variant'
                        }`}
                      >
                        {delman}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {createError && (
                <div role="alert" className="alert-error">
                  <span className="material-symbols-outlined text-base shrink-0">error</span>
                  <span>{createError}</span>
                </div>
              )}
                </>
              )}
            </div>
            <div className="px-4 py-3 border-t border-surface-variant flex gap-2 justify-end shrink-0">
              <button type="button" className="btn-outline" onClick={() => setIsCreateOpen(false)}>
                {kitchenTerms.dispatch.create.cancel}
              </button>
              {!hasNoKitchenStock && (
              <button
                type="button"
                className="btn-primary"
                onClick={requestGenerateShipments}
                disabled={isGenerating || isLoadingCreateOutletPlans}
              >
                {isGenerating ? kitchenTerms.dispatch.generating : kitchenTerms.dispatch.create.generate}
              </button>
              )}
            </div>
          </div>
        </div>
      )}

      <PinKeypadModal
        open={isPinModalOpen}
        title={kitchenTerms.dispatch.pin.title}
        description={kitchenTerms.dispatch.pin.description}
        confirmLabel={kitchenTerms.dispatch.pin.confirm}
        onClose={() => setIsPinModalOpen(false)}
        onConfirm={confirmHandoverToDelman}
      />

      {dadakanWarning && (
        <div className="fixed inset-0 z-40 bg-inverse-surface/40 flex items-center justify-center p-4">
          <div
            role="alertdialog"
            aria-labelledby="dadakan-warning-title"
            aria-describedby="dadakan-warning-body"
            className="w-full max-w-md bg-surface-card border border-status-warning/40 rounded-xl shadow-sm"
          >
            <div className="px-4 py-3 border-b border-surface-variant">
              <p id="dadakan-warning-title" className="text-headline-md text-on-surface">
                {kitchenTerms.dispatch.dadakanWarning.title}
              </p>
            </div>
            <div className="p-4 space-y-3">
              <div className="flex items-start gap-3 rounded-lg border border-status-warning/40 bg-status-warning-bg/30 p-3">
                <span className="material-symbols-outlined text-status-warning text-2xl shrink-0">
                  warning
                </span>
                <p id="dadakan-warning-body" className="text-body-sm text-on-surface">
                  {dadakanWarning.action === 'generate'
                    ? kitchenTerms.dispatch.dadakanWarning.bodyGenerate
                    : kitchenTerms.dispatch.dadakanWarning.bodyHandover}
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-label-md font-semibold text-on-surface-variant">
                  {kitchenTerms.dispatch.dadakanWarning.storesLabel}
                </p>
                <ul className="space-y-1">
                  {dadakanWarning.conflicts.map((c) => (
                    <li
                      key={`${c.storeName}-${c.products.join(',')}`}
                      className="text-body-sm text-on-surface rounded-lg bg-surface-container px-3 py-2"
                    >
                      {kitchenTerms.dispatch.dadakanWarning.storeLine(c.storeName, c.products)}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="px-4 py-3 border-t border-surface-variant flex gap-2 justify-end">
              <button
                type="button"
                className="btn-secondary min-h-[44px]"
                onClick={() => setDadakanWarning(null)}
              >
                {kitchenTerms.dispatch.dadakanWarning.cancel}
              </button>
              <button
                type="button"
                className="btn-primary min-h-[44px]"
                onClick={continueAfterDadakanWarning}
              >
                {kitchenTerms.dispatch.dadakanWarning.continue}
              </button>
            </div>
          </div>
        </div>
      )}

      {generateResult && (
        <GenerateResultSheet
          open
          result={generateResult}
          selectedOutletCount={generateResultOutletCount}
          tripLabel={generateResultTripLabel}
          onClose={() => setGenerateResult(null)}
          onToProduction={() => {
            setGenerateResult(null)
            router.push('/kitchen/production')
          }}
          onViewShipment={() => {
            const result = generateResult
            setGenerateResult(null)
            setListFilter('pending')
            if (generateResultOutletCount === 1 && result.deliveries.length === 1) {
              openShipment(result.deliveries[0].delivery_code)
            }
          }}
        />
      )}

      {toast && <Toast message={toast} onDismiss={dismissToast} />}
    </section>
  )
}
